import React, { useState, useEffect } from "react";
import {
	Calendar,
	MapPin,
	User,
	Loader,
	X,
	Eye,
	Download,
	AlertCircle,
	AlertTriangleIcon,
} from "lucide-react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useTranslation } from "../i18n/TranslationContext";
import { useGetExtraEvents } from "@/api/extraEvents/useGetExtraEvents";
import { useCancelEvent, useGetExtra, useCurrentUser } from "../api";
import { useProbebetrieb } from "@/api/probebetrieb/useProbebetrieb";
import {
	calculateDaysRemaining,
	formatTimeRange,
	formatDateDisplay,
	formatDate,
	parseEventDate,
} from "../utils";

// Cancel Modal Component
function EventCancelModal({
	isOpen,
	onClose,
	onConfirm,
}: {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (reason: string) => void;
}) {
	const { t } = useTranslation();
	const [selectedReason, setSelectedReason] = useState("");
	const [customReason, setCustomReason] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const cancellationReasons = [
		{
			id: "reason1",
			label: t("combinedEventDashboard.cancellation.reasons.personalEmergency"),
		},
		{
			id: "reason2",
			label: t("combinedEventDashboard.cancellation.reasons.scheduleConflict"),
		},
		{
			id: "reason3",
			label: t("combinedEventDashboard.cancellation.reasons.healthIssues"),
		},
		{
			id: "reason4",
			label: t(
				"combinedEventDashboard.cancellation.reasons.transportationProblems"
			),
		},
		{
			id: "reason5",
			label: t("combinedEventDashboard.cancellation.reasons.familyObligations"),
		},
		{
			id: "reason6",
			label: t("combinedEventDashboard.cancellation.reasons.workConflict"),
		},
		{
			id: "other",
			label: t("combinedEventDashboard.cancellation.reasons.other"),
		},
	];

	const handleReasonSelect = (reasonId: string) => {
		setSelectedReason(reasonId);
		// Clear custom reason if not selecting "Other"
		if (reasonId !== "other") {
			setCustomReason("");
		}
	};

	const handleConfirm = async () => {
		if (!selectedReason) return;

		// If "Other" is selected, make sure custom reason is provided
		if (selectedReason === "other" && !customReason.trim()) return;

		setIsSubmitting(true);

		try {
			// Process cancellation with appropriate reason
			const reasonToSend =
				selectedReason === "other" ? customReason : selectedReason;
			onConfirm(reasonToSend);

			// Reset state
			setSelectedReason("");
			setCustomReason("");
			onClose();
		} catch (error) {
			console.error("Cancellation failed:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleClose = () => {
		if (!isSubmitting) {
			setSelectedReason("");
			setCustomReason("");
			onClose();
		}
	};

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			handleClose();
		}
	};

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && isOpen && !isSubmitting) {
				handleClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscape);
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "auto";
		};
	}, [isOpen, isSubmitting]);

	if (!isOpen) return null;

	return (
		<div
			className="cancel-modal-overlay fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] bg-opacity-40 flex justify-center items-center z-[1000] p-5"
			onClick={handleOverlayClick}
		>
			<div className="cancel-modal-container bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.2)] max-w-[480px] w-full max-h-[80vh] overflow-y-auto relative">
				{/* Modal Header */}
				<div className="modal-header flex justify-between items-center p-6 pb-4 border-b border-[var(--ucm-border-light)]">
					<h2 className="modal-title text-xl font-semibold text-[var(--ucm-text-dark)] m-0">
						{t("combinedEventDashboard.cancellation.title")}
					</h2>
					<button
						onClick={handleClose}
						disabled={isSubmitting}
						className="modal-close-btn bg-[var(--ucm-light-gray)] border-none w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors hover:bg-[var(--ucm-border-medium)]"
					>
						<X size={20} className="text-[var(--ucm-text-medium)]" />
					</button>
				</div>

				{/* Modal Content */}
				<div className="modal-content p-6">
					<h3 className="cancel-question text-lg font-semibold text-[var(--ucm-text-dark)] m-0 mb-6">
						{t("combinedEventDashboard.cancellation.question")}
					</h3>

					{/* Cancellation Reasons */}
					<div className="reason-selection-list flex flex-col gap-1 mb-[28px] mt-[0px] mr-[0px] ml-[0px]">
						{cancellationReasons.map((reason) => (
							<div
								key={reason.id}
								className={`reason-item flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
									selectedReason === reason.id
										? "border-[var(--ucm-primary-teal)] bg-[var(--ucm-light-blue)] selected"
										: "border-[var(--ucm-border-light)] bg-white hover:border-[var(--ucm-primary-teal)] hover:bg-[var(--ucm-light-blue)]"
								}`}
								onClick={() => handleReasonSelect(reason.id)}
							>
								<input
									type="radio"
									id={reason.id}
									name="cancelReason"
									value={reason.id}
									checked={selectedReason === reason.id}
									onChange={() => handleReasonSelect(reason.id)}
									className="reason-radio w-5 h-5 mr-4 accent-[var(--ucm-primary-teal)] cursor-pointer"
								/>
								<label
									htmlFor={reason.id}
									className="reason-label text-base font-medium text-[var(--ucm-text-dark)] cursor-pointer flex-1 m-0"
								>
									{reason.label}
								</label>
							</div>
						))}
					</div>

					{/* Custom reason input for "Other" option */}
					{selectedReason === "other" && (
						<div className="custom-reason-container mt-4">
							<textarea
								value={customReason}
								onChange={(e) => setCustomReason(e.target.value)}
								placeholder={t(
									"combinedEventDashboard.cancellation.specifyReason"
								)}
								className="w-full p-3 border-2 border-[var(--ucm-border-light)] rounded-xl resize-none text-base text-[var(--ucm-text-dark)] placeholder-[var(--ucm-text-medium)] focus:outline-none focus:border-[var(--ucm-primary-teal)] transition-colors"
								rows={3}
								maxLength={200}
								disabled={isSubmitting}
							/>
							<div className="text-xs text-[var(--ucm-text-medium)] mt-1 text-right">
								{customReason.length}/200{" "}
								{t("combinedEventDashboard.cancellation.characters")}
							</div>
						</div>
					)}

					{/* Action Buttons */}
					<div className="modal-actions flex justify-center pt-4 border-t border-[var(--ucm-border-light)]">
						<button
							onClick={handleConfirm}
							disabled={
								!selectedReason ||
								isSubmitting ||
								(selectedReason === "other" && !customReason.trim())
							}
							className={`confirm-cancel-btn w-full border-none rounded-xl p-4 text-base font-semibold cursor-pointer transition-all duration-200 ${
								selectedReason &&
								!isSubmitting &&
								(selectedReason !== "other" || customReason.trim())
									? "bg-[var(--ucm-error-red)] text-white hover:bg-[#dc2626] hover:-translate-y-0.5"
									: "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed"
							}`}
						>
							{isSubmitting ? (
								<div className="flex items-center justify-center">
									<Loader size={16} className="animate-spin mr-2" />
									{t("combinedEventDashboard.cancellation.cancelling")}
								</div>
							) : selectedReason === "other" && !customReason.trim() ? (
								t("combinedEventDashboard.cancellation.enterReason")
							) : selectedReason ? (
								t("combinedEventDashboard.cancellation.confirm")
							) : (
								t("combinedEventDashboard.cancellation.selectReason")
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

export function CombinedEventDashboard() {
	const { t } = useTranslation();
	const {
		jobs,
		setJobs,
		setCurrentScreen,
		setNavigationContext,
		navigationContext,
		updateOnboardingState,
	} = useApp();
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showPdfModal, setShowPdfModal] = useState(false);
	const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

	// Get current user to get dynamic extraId
	const { user } = useCurrentUser();
	const extraId = user?.extraId || 0;

	// Use the hook to fetch events with dynamic extraId
	// Determine event type based on user's internal status
	const eventType = user?.internal_extra ? "internal" : "external";
	const {
		events: extraEvents,
		error: eventsError,
		loading: eventsLoading,
		refetch: fetchExtraEvents,
	} = useGetExtraEvents(extraId, eventType);

	// Use the hook to fetch extra data for the name
	const { extra } = useGetExtra(extraId);

	// Use the cancellation hook
	const { cancelEvent, loading: cancelLoading } = useCancelEvent();

	// Use the Probebetrieb hook
	const eventIdForProbebetrieb = extraEvents?.[0]?.event_id || null;
	const {
		visibility: probebetriebVisibility,
		downloadPDF,
		downloading,
	} = useProbebetrieb(eventIdForProbebetrieb, extraId);

	const handlePreviewPDF = async () => {
		if (!eventIdForProbebetrieb || !extraId) return;

		try {
			// Fetch the PDF with authentication headers
			const apiUrl =
				import.meta.env.VITE_API_URL || "http://localhost:3001/api";
			const token = localStorage.getItem("accessToken");

			const response = await fetch(
				`${apiUrl}/probebetrieb/preview/${eventIdForProbebetrieb}/${extraId}`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (!response.ok) {
				const errorData = await response.json();
				console.error("Preview failed:", errorData);
				return;
			}

			// Create blob URL and show in modal
			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			setPdfBlobUrl(blobUrl);
			setShowPdfModal(true);
		} catch (error) {
			console.error("Error previewing PDF:", error);
		}
	};

	const handleClosePdfModal = () => {
		setShowPdfModal(false);
		// Clean up blob URL
		if (pdfBlobUrl) {
			window.URL.revokeObjectURL(pdfBlobUrl);
			setPdfBlobUrl(null);
		}
	};
	// Set hasCompletedOnboarding to true when user reaches this screen
	useEffect(() => {
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
		});
	}, []);

	useEffect(() => {
		// Convert extra events to job format
		if (extraEvents && extraEvents.length > 0) {
			const convertedJobs = extraEvents.map((event) => {
				return {
					id: event.event_id?.toString() || "0",
					title: "Frankfurt Terminal 3 - Probebetrieb",
					location:
						"Flughafen Frankfurt, Terminal 3 (Informationen zur Anreise folgen)",
					date: event.event_date || new Date().toISOString().split("T")[0], // Keep the original date string from backend
					startTime: event.start_time, // Store start time separately for timezone handling
					time: formatTimeRange(event.start_time, event.end_time),
					status: "check-in-required" as const,
					eventStatus: event.event_status,
					checkIn: event.check_in,
					checkOut: event.check_out,
					idCheck: event.id_check,
					company: "Flughafensicherheit",
				};
			});
			setJobs(convertedJobs);
		} else if (!eventsLoading && !eventsError) {
			// No events found, set empty array
			setJobs([]);
		}
	}, [extraEvents, eventsLoading, eventsError, setJobs]);

	// Get the first (and only) job and event
	const currentJob = jobs[0];
	const currentEvent = extraEvents[0];

	const handleTaskList = () => {
		if (currentJob?.status === "check-in-required") {
			// Store navigation context before navigating to task list
			setNavigationContext({
				sourceScreen: "my-event",
				sourceEventId: currentJob.id,
			});
			setCurrentScreen("task-list");
		}
	};

	const canAccessTaskList = () => {
		return currentJob?.status === "check-in-required";
	};

	const handleAddToCalendar = () => {
		if (!currentEvent) return;

		// Format the event details for calendar
		const eventTitle = `Flughafen Frankfurt Terminal 3 Probebetrieb`;
		const eventLocation = `Flughafen Frankfurt, Terminal 3 (Informationen zur Anreise folgen)`;

		// Parse the date and times
		const eventDate = parseEventDate(currentEvent.event_date) || new Date(currentEvent.event_date);
		const year = eventDate.getFullYear();
		const month = (eventDate.getMonth() + 1).toString().padStart(2, "0");
		const day = eventDate.getDate().toString().padStart(2, "0");

		// Parse start and end times
		const [startHour, startMin] = (currentEvent.start_time || "10:00:00")
			.split(":")
			.map(Number);
		const [endHour, endMin] = (currentEvent.end_time || "13:30:00")
			.split(":")
			.map(Number);

		// Create start and end datetime strings in the format required by Google Calendar
		const startDateTime = new Date(
			year,
			parseInt(month) - 1,
			parseInt(day),
			startHour,
			startMin
		);
		const endDateTime = new Date(
			year,
			parseInt(month) - 1,
			parseInt(day),
			endHour,
			endMin
		);

		// Format dates for Google Calendar (YYYYMMDDTHHmmss)
		const formatDateForGoogle = (date: Date) => {
			const y = date.getFullYear();
			const m = (date.getMonth() + 1).toString().padStart(2, "0");
			const d = date.getDate().toString().padStart(2, "0");
			const h = date.getHours().toString().padStart(2, "0");
			const min = date.getMinutes().toString().padStart(2, "0");
			const s = "00";
			return `${y}${m}${d}T${h}${min}${s}`;
		};

		const googleStartDate = formatDateForGoogle(startDateTime);
		const googleEndDate = formatDateForGoogle(endDateTime);

		// Event description
		const eventDescription = `Bitte erscheinen Sie pünktlich zu Ihrer Schicht am Terminal 3 des Frankfurter Flughafens.`;

		// Create Google Calendar URL
		const googleCalendarUrl =
			`https://calendar.google.com/calendar/render?action=TEMPLATE` +
			`&text=${encodeURIComponent(eventTitle)}` +
			`&dates=${googleStartDate}/${googleEndDate}` +
			`&details=${eventDescription}` +
			`&location=${encodeURIComponent(eventLocation)}`;

		// Open Google Calendar in a new tab
		window.open(googleCalendarUrl, "_blank");
	};

	const handleCancelEvent = () => {
		setShowCancelModal(true);
	};

	const handleConfirmCancellation = async (reason: string) => {
		if (!currentEvent) return;

		try {
			// Call the real cancellation API
			const result = await cancelEvent({
				extra_id: currentEvent.extra_id,
				event_id: currentEvent.event_id,
				reason: reason,
			});

			if (result) {
				// After successful cancellation, refetch events to get updated status
				await fetchExtraEvents();
			}
		} catch (error) {
			console.error("Cancellation failed:", error);
		}
	};

	// Show loading state while fetching events
	if (eventsLoading) {
		return (
			<MainLayout showBack showBottomNav={false}>
				<div className="flex flex-col items-center justify-center py-8">
					<Loader className="w-8 h-8 animate-spin text-[var(--ucm-primary-teal)] mb-4" />
					<p className="text-[var(--ucm-text-medium)]">
						{t("combinedEventDashboard.loadingEvents")}
					</p>
				</div>
			</MainLayout>
		);
	}

	// Show error state if fetching failed
	if (eventsError) {
		return (
			<MainLayout showBack showBottomNav={false}>
				<div className="text-center py-8">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-4">
						{t("combinedEventDashboard.title")}
					</h1>
					<p className="text-[var(--ucm-error-red)] mb-4">
						{t("errors.errorLoading")}: {eventsError}
					</p>
					<button
						onClick={fetchExtraEvents}
						className="px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-lg hover:bg-[var(--ucm-primary-teal-hover)]"
					>
						{t("common.tryAgain")}
					</button>
				</div>
			</MainLayout>
		);
	}

	// Show empty state if no events
	if (!currentJob || jobs.length === 0) {
		return (
			<MainLayout showBack showBottomNav={false}>
				<div className="flex flex-col h-full">
					{/* Header */}
					<div className="bg-gradient-to-b to-[var(--ucm-light-gray)] px-4 py-6">
						<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
							{t("combinedEventDashboard.noEventsTitle")}
						</h1>
						<p className="text-[var(--ucm-text-medium)]">
							{t("combinedEventDashboard.eventDetailScreen")}
						</p>
					</div>

					{/* Message Container */}
					<div className="flex items-center justify-center px-4 py-8">
						<div className="bg-white rounded-2xl p-8 shadow-[0_4px_16px_rgba(0,0,0,0.08)] max-w-md text-center">
							{/* Icon */}
							<div className="mb-6">
								<div className="w-20 h-20 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center mx-auto">
									<Calendar
										size={32}
										className="text-[var(--ucm-primary-teal)]"
									/>
								</div>
							</div>

							{/* Message */}
							<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">
								{t("combinedEventDashboard.noEventsMessage")}
							</h2>
							<p className="text-[var(--ucm-text-medium)] mb-6">
								{t("combinedEventDashboard.noEventsSubMessage")}
							</p>

							{/* Refresh Button */}
							<button
								onClick={fetchExtraEvents}
								className="w-full px-6 py-3 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200"
							>
								{t("common.refresh")}
							</button>
						</div>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack showBottomNav={false}>
			{/* Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-4">
					{t("combinedEventDashboard.eventDetailScreen")}
				</h1>

				{/* Job Header */}
				<div className="mb-4 flex flex-row items-center justify-between">
					<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)]">
						{currentJob.title}
					</h2>
					<span className="text-sm text-[var(--ucm-text-medium)]">
						{calculateDaysRemaining(currentJob.date)}
					</span>
				</div>
			</div>

			{/* Job Information Card */}
			<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
				<div className="space-y-4">
					<div className="flex items-center space-x-4">
						<div className="w-8 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center">
							<Calendar size={16} className="text-[var(--ucm-dark-blue)]" />
						</div>
						<div className="flex items-center space-x-4">
							<span className="text-[var(--ucm-text-dark)]">
								{formatDateDisplay(currentJob.date)}
							</span>
							<span className="text-[var(--ucm-text-medium)]">
								{currentJob.time}
							</span>
						</div>
					</div>

					<div className="flex items-center space-x-4">
						<div className="w-10 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center">
							<MapPin size={16} className="text-[var(--ucm-dark-blue)]" />
						</div>
						<span className="text-[var(--ucm-text-dark)]">
							{currentJob.location}
						</span>
					</div>

					<div className="flex items-center space-x-4">
						<div className="w-8 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center">
							<User size={16} className="text-[var(--ucm-dark-blue)]" />
						</div>
						<span className="text-[var(--ucm-text-dark)]">
							{extra
								? `${extra.first_name} ${extra.last_name}`
								: t("combinedEventDashboard.loading")}
						</span>
					</div>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="flex items-center space-x-3 mb-6">
				{canAccessTaskList() && (
					<button
						onClick={handleTaskList}
						className="flex-1 h-12 bg-[var(--ucm-success-green)] text-white font-semibold rounded-full hover:bg-[var(--ucm-dark-green)] transition-colors duration-200"
					>
						{t("jobDetail.taskList")}
					</button>
				)}
				<button
					onClick={handleAddToCalendar}
					title={t("combinedEventDashboard.addToGoogleCalendar")}
					aria-label={t("combinedEventDashboard.addEventToGoogleCalendar")}
					className="w-12 h-12 bg-white border border-[var(--ucm-border-light)] rounded-full flex items-center justify-center transition-colors hover:bg-gray-50 hover:border-[var(--ucm-primary-teal)]"
				>
					<Calendar size={20} className="text-[var(--ucm-text-medium)]" />
				</button>

				{/* Task List Access Info */}
				{!canAccessTaskList() && (
					<div className="flex-1 bg-[var(--ucm-light-blue)] border border-[var(--ucm-border-light)] rounded-2xl p-4 h-12 flex items-center">
						<p className="text-sm text-[var(--ucm-dark-blue)] text-center flex-1">
							{t("jobDetail.taskListAvailable")}
						</p>
					</div>
				)}
			</div>

			{/* Job Detail Section */}
			<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
				<h3 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">
					Ihre Rolle im Probebetrieb
				</h3>

				<div className="space-y-4 text-[var(--ucm-text-dark)]">
					<p>
						Vor Ort übernehmen Sie die Rolle einer Testpassagierin bzw. eines
						Testpassagiers und spielen typische Abläufe im Terminal durch – zum
						Beispiel Check-in, Sicherheitskontrolle, Boarding oder die
						Gepäckabholung. Auf diese Weise helfen Sie dabei, Prozesse und Wege
						im neuen Terminal zu testen.
					</p>

					<p>
						<strong>Wichtig</strong>
					</p>

					<ul className="list-disc space-y-1 ml-8">
						<li>
							{extra?.internal_extra
								? "Bringen Sie Ihren Flughafenausweis mit (für die Identifizierung)."
								: "Bitte bringen Sie ein gültiges Ausweisdokument mit (Reisepass oder Personalausweis)"}
						</li>
						<li>Bitte bringen Sie <b>kein</b> eigenes Gepäck mit. Erlaubt ist ausschließlich eine Handtasche oder ein kleiner Rucksack für persönliche Zwecke in DIN-A4-Größe.</li>
						<li>Verpflegung, Materialien und Ihre detaillierten Aufgaben erhalten Sie vor Ort</li>
						<li>Weitere Informationen zum Ablauf erhalten Sie per E-Mail. Die Anreiseinformationen werden eine Woche vor Termin hier verfügbar sein.</li>
					</ul>

					<p>
						Nach Abschluss des Probebetriebs werden Sie Ihre Erfahrung als Passagier im Terminal 3 über unser Feeback Formular bewerten.
					</p>
				</div>
			</div>

			{/* Probebetrieb PDF Download Section */}
			{/* Shows for all events within 7 days */}
			{probebetriebVisibility?.showProbebetrieb && (
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
					<h3 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">
						Anreiseinformationen für den Probebetrieb
					</h3>
					{probebetriebVisibility.pdfComingSoon ? (
						<>
							<p className="text-[var(--ucm-text-dark)] mb-4 leading-relaxed">
								Ihr Termin findet in {probebetriebVisibility.daysUntilEvent}{" "}
								{probebetriebVisibility.daysUntilEvent === 1 ? "Tag" : "Tagen"}{" "}
								statt. Die Anreisedetails werden in Kürze hier verfügbar sein.
							</p>
							<div className="flex items-center justify-center py-4 px-6 bg-[var(--ucm-light-gray)] rounded-xl">
								<AlertCircle size={20} className="text-[var(--ucm-text-medium)] mr-2" />
								<span className="text-[var(--ucm-text-medium)] font-medium">
									Bald verfügbar
								</span>
							</div>
						</>
					) : (
						<>
							<p className="text-[var(--ucm-text-dark)] mb-4 leading-relaxed">
								Ihr Termin findet in {probebetriebVisibility.daysUntilEvent}{" "}
								{probebetriebVisibility.daysUntilEvent === 1 ? "Tag" : "Tagen"}{" "}
								statt. Bitte laden Sie die Anreisedetails für den Probebetrieb im
								Terminal 3 herunter und lesen Sie sich diese aufmerksam durch.
							</p>

							{/* Video section - only for dates that are NOT 27.01 or 29.01 */}
							{(() => {
								// eventDate comes as YYYY-MM-DD string from backend
								const eventDateStr = String(probebetriebVisibility.eventDate);
								// Extract MM-DD from YYYY-MM-DD
								const monthDay = eventDateStr.substring(5); // Gets "MM-DD"
								const isSpecialDate = monthDay === "01-27" || monthDay === "01-29";

								if (!isSpecialDate) {
									return (
										<div className="mb-4">
											<p className="text-[var(--ucm-text-dark)] mb-3 leading-relaxed">
												Bitte schauen Sie sich auch das folgende Anreisevideo an:
											</p>
											<a
												href="https://www.youtube.com/watch?v=_-QlIcsAHVM"
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center justify-center space-x-2 px-4 py-3 bg-[#FF0000] text-white rounded-xl hover:bg-[#CC0000] transition-colors mb-4"
											>
												<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
													<path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
												</svg>
												<span>Anreisevideo ansehen</span>
											</a>
										</div>
									);
								}
								return null;
							})()}

							<div className="flex space-x-3">
								<button
									onClick={handlePreviewPDF}
									className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-[var(--ucm-primary-teal)] text-[var(--ucm-primary-teal)] rounded-xl hover:bg-[var(--ucm-light-blue)] transition-colors"
								>
									<Eye size={20} />
									<span>
										Vorschau
										<br />
										Anreisedetails
									</span>
								</button>
								<button
									onClick={downloadPDF}
									disabled={downloading}
									className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<Download size={20} />
									<span>
										{downloading
											? "Wird heruntergeladen..."
											: "Download Anreisedetails"}
									</span>
								</button>
							</div>
						</>
					)}
				</div>
			)}

			{/* Profile Status Warning */}
			{extra?.profile_status !== "ready_to_work" && (
				<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
					<div className="flex items-center gap-1 mb-2">
						<AlertTriangleIcon className="w-5 h-5 text-yellow-800" />
						<h3 className="text-sm font-semibold text-yellow-800">
							Profil unvollständig
						</h3>
					</div>

					<p className="text-sm text-yellow-700">
						Bitte vervollständigen Sie Ihr Profil, um Termine auswählen zu
						können.
					</p>
				</div>
			)}

			{/* Cancel Button or Group Booking Info */}
			{!currentEvent?.can_cancel && (
				<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
					<div className="flex items-center gap-1 mb-2">
						<AlertTriangleIcon className="w-5 h-5 text-yellow-800" />
						<h3 className="text-sm font-semibold text-yellow-800">
							{t("combinedEventDashboard.groupBookingTitle")}
						</h3>
					</div>

					<p className="text-sm text-yellow-700">
						{t("combinedEventDashboard.groupBookingMessage")}
					</p>
				</div>
			)}

			<button
				onClick={handleCancelEvent}
				disabled={
					extra?.profile_status !== "ready_to_work" || !currentEvent?.can_cancel
				}
				className={`cancel-event-btn w-full bg-transparent border-2 rounded-xl p-4 text-base font-medium transition-all duration-200 mt-6 ${
					extra?.profile_status !== "ready_to_work" || !currentEvent?.can_cancel
						? "border-gray-200 text-gray-400 cursor-not-allowed"
						: "border-[var(--ucm-border-light)] text-[var(--ucm-text-dark)] cursor-pointer hover:border-[var(--ucm-error-red)] hover:text-[var(--ucm-error-red)] hover:bg-red-50"
				}`}
			>
				{t("combinedEventDashboard.cancelThisEvent")}
			</button>

			{/* Cancel Modal */}
			<EventCancelModal
				isOpen={showCancelModal}
				onClose={() => !cancelLoading && setShowCancelModal(false)}
				onConfirm={handleConfirmCancellation}
			/>

			{/* PDF Preview Modal */}
			{showPdfModal && pdfBlobUrl && (
				<div
					className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.75)] flex justify-center items-center z-[1000] p-4"
					onClick={(e) => {
						if (e.target === e.currentTarget) {
							handleClosePdfModal();
						}
					}}
				>
					<div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] w-full max-w-5xl h-[90vh] flex flex-col">
						{/* Modal Header */}
						<div className="flex justify-between items-center p-4 border-b border-[var(--ucm-border-light)]">
							<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)]">
								Anreiseinformationen für den Probebetrieb
							</h2>
							<button
								onClick={handleClosePdfModal}
								className="bg-[var(--ucm-light-gray)] border-none w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors hover:bg-[var(--ucm-border-medium)]"
								aria-label="Schließen"
							>
								<X size={20} className="text-[var(--ucm-text-medium)]" />
							</button>
						</div>

						{/* PDF Viewer */}
						<div className="flex-1 overflow-hidden">
							<iframe
								src={pdfBlobUrl}
								className="w-full h-full border-none"
								title="Probebetrieb PDF"
							/>
						</div>
					</div>
				</div>
			)}
		</MainLayout>
	);
}
